/**
 * WebSocket client for the duel rooms system
 * Provides real-time updates for active rooms, chat messages, and player status
 */

class WebSocketRoomClient {
  constructor() {
    this.ws = null;
    this.connected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000; // 3 seconds
    this.pingInterval = null;
    this.lastConnectionError = null;
    this.userId = null;
    this.listeners = {
      roomsUpdate: new Set(),
      roomCreated: new Set(),
      roomUpdated: new Set(),
      roomClosed: new Set(),
      chatMessage: new Set(),
      connection: new Set(),
      error: new Set(),
      gameLaunch: new Set(),
      gameStartBroadcast: new Set(),
      roomStatusReset: new Set(), // New event type for handling room status resets
      walletBalanceUpdate: new Set() // Added for wallet balance updates
    };

    // Start heartbeat mechanism automatically
    this.startHeartbeat();
  }

  /**
   * Connect to the WebSocket server
   * @param {string} userId - User ID for identification
   * @returns {Promise} - Resolves when connected
   */
  connect(userId) {
    return new Promise((resolve, reject) => {
      if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected');
        resolve();
        return;
      }

      this.userId = userId;
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';

      // Use the dedicated WebSocket port for duel rooms (8765)
      // This is the port where the duel room WebSocket server is running
      const port = 8765;

      // Use the correct path format for the duel room WebSocket server
      // The server supports both /stream/ and /ws/stream/ paths
      const wsUrl = `${protocol}://${window.location.hostname}:${port}/stream/${userId}`; // Use user ID in path

      console.log('Connecting to Stream WebSocket server:', wsUrl);
      try {
        this.ws = new WebSocket(wsUrl);
      } catch (error) {
        console.error('Error creating WebSocket connection:', error);
        reject(error);
        return;
      }

      this.ws.onopen = () => {
        console.log('WebSocket connection established');
        this.connected = true;
        this.reconnectAttempts = 0;
        this.lastConnectionError = null;

        // Notify connection listeners
        this.emit('connection', { connected: true });

        // Send a request to get the latest active rooms
        this.sendMessage({ type: 'GET_ROOMS' });

        // Start heartbeat
        this.startHeartbeat();

        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
          this.emit('error', { error: 'Failed to parse WebSocket message' });
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket connection closed', event.code ? `with code ${event.code}` : '');
        this.connected = false;

        // Stop the heartbeat
        this.stopHeartbeat();

        // Notify connection listeners
        this.emit('connection', { connected: false });

        // Don't attempt to reconnect if this was a normal closure (code 1000)
        // or if we've reached the maximum number of reconnect attempts
        if (event.code === 1000) {
          console.log('WebSocket closed normally, not reconnecting');
          return;
        }

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          // Use exponential backoff for reconnection attempts
          const delay = Math.min(this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts), 30000);
          console.log(`Reconnecting in ${delay / 1000} seconds... (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

          setTimeout(() => {
            this.reconnectAttempts++;
            this.connect(this.userId).catch(error => {
              console.error('Reconnection failed:', error);
            });
          }, delay);
        } else {
          console.error('Maximum reconnection attempts reached');
          this.emit('error', {
            error: 'Maximum reconnection attempts reached',
            message: 'Could not reconnect to the server after multiple attempts. Please refresh the page.'
          });
          reject(new Error('Maximum reconnection attempts reached'));
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.lastConnectionError = error;

        // Create a more detailed error message
        const errorDetails = {
          error: 'WebSocket connection error',
          message: 'Failed to connect to WebSocket server: ' +
            'This may be due to network issues or the server being unavailable. ' +
            'Check if the WebSocket server is running on port 8765.',
          timestamp: new Date().toISOString(),
          url: wsUrl,
          reconnectAttempts: this.reconnectAttempts,
          maxReconnectAttempts: this.maxReconnectAttempts
        };

        this.emit('error', errorDetails);

        // Only reject the promise on the first error
        // For subsequent errors during reconnection attempts, we don't want to reject again
        if (this.reconnectAttempts === 0) {
          reject(errorDetails);
        }
      };
    });
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect() {
    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.connected = false;
      console.log('WebSocket disconnected');
    }
  }

  /**
   * Start sending heartbeat pings to keep the connection alive
   */
  startHeartbeat() {
    this.stopHeartbeat(); // Clear any existing interval

    // Track when we last received a pong response
    this.lastPongTime = Date.now();

    // Send a ping every 30 seconds to keep the connection alive
    this.pingInterval = setInterval(() => {
      if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Check if we've received a pong since the last ping
        const timeSinceLastPong = Date.now() - this.lastPongTime;

        // If it's been more than 2 minutes since the last pong, the connection might be dead
        if (timeSinceLastPong > 120000) { // 2 minutes
          console.warn(`No pong received for ${timeSinceLastPong / 1000} seconds, connection may be dead`);

          // Close the connection and let the onclose handler handle reconnection
          try {
            this.ws.close();
          } catch (error) {
            console.error('Error closing potentially dead WebSocket connection:', error);
          }
          return;
        }

        // Send a ping with the current timestamp
        this.sendMessage({
          type: 'PING',
          timestamp: new Date().toISOString()
        });
        console.log('Sent heartbeat ping');
      }
    }, 30000); // 30 seconds
  }

  /**
   * Stop the heartbeat mechanism
   */
  stopHeartbeat() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Send a message to the WebSocket server
   * @param {Object} message - Message to send
   */
  sendMessage(message) {
    if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('Cannot send message, WebSocket not connected');
      return false;
    }

    try {
      this.ws.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      this.emit('error', { error: 'Failed to send WebSocket message' });
      return false;
    }
  }

  /**
   * Request a fresh list of rooms from the server
   * Uses throttling to prevent excessive requests
   */
  lastRefreshTimestamp = 0;
  refreshRooms() {
    const currentTime = Date.now();
    // Throttle requests to one every 5 seconds
    if (currentTime - this.lastRefreshTimestamp < 5000) {
      console.log('Throttling GET_ROOMS request - too soon since last request');
      return false;
    }

    this.lastRefreshTimestamp = currentTime;
    return this.sendMessage({
      type: 'GET_ROOMS',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Handle incoming messages from the WebSocket server
   * @param {Object} message - Parsed message object
   */
  handleMessage(message) {
    // Global message debug logging
    console.log(`[WebSocketRoomClient] Received message type: ${message.type}`);

    switch (message.type) {
      case 'ROOMS_LIST':
        // Update active rooms list
        console.log('Received active rooms list:', message.data);
        this.emit('roomsUpdate', message.data);
        break;

      case 'ROOM_CREATED':
        // New room created
        console.log('New room created:', message.data);
        this.emit('roomCreated', message.data);
        break;

      case 'WALLET_BALANCE_UPDATE':
        // Wallet balance update
        console.log('Received wallet balance update:', message.data);
        this.emit('walletBalanceUpdate', message.data);
        break;

      case 'ROOM_UPDATED':
        // Room updated (participants, status, etc.)
        console.log('Room updated:', message.data);
        this.emit('roomUpdated', message.data);
        break;

      case 'ROOM_CLOSED':
        // Room closed
        console.log('Room closed:', message.data.roomId);
        this.emit('roomClosed', message.data);
        break;

      case 'CHAT_MESSAGE':
        // New chat message
        console.log('New chat message:', message.data);
        this.emit('chatMessage', message.data);
        break;

      case 'GAME_LAUNCH':
        // Game is ready to launch
        console.log('🎮 GAME_LAUNCH MESSAGE RECEIVED:', message.data);

        // Emit the event first so subscribers can react
        this.emit('gameLaunch', message.data);

        // Navigate to the game URL - NO TIMEOUT, do it immediately
        if (message.data && message.data.url) {
          try {
            // Construct full URL if it's relative
            let gameUrl = message.data.url;

            // Check URL structure and prepare for redirection
            if (gameUrl.startsWith('/')) {
              console.log('Using relative URL:', gameUrl);
            } else {
              console.log('Using absolute URL:', gameUrl);
            }

            // Add critical parameters for multiplayer if they're missing
            // (especially for room creator)
            if (message.data.roomId && this.userId) {
              const url = new URL(gameUrl, window.location.origin);

              // Add roomId if not present
              if (!url.searchParams.has('roomID')) {
                url.searchParams.set('roomID', message.data.roomId);
              }

              // Add userId if not present (critical for multiplayer)
              if (!url.searchParams.has('userID')) {
                url.searchParams.set('userID', this.userId);
              }

              // Add challenger and rival if available
              if (message.data.challenger && !url.searchParams.has('challenger')) {
                url.searchParams.set('challenger', message.data.challenger);
              }

              if (message.data.rival && !url.searchParams.has('rival')) {
                url.searchParams.set('rival', message.data.rival);
              }

              // Add role if available
              if (message.data.role && !url.searchParams.has('role')) {
                url.searchParams.set('role', message.data.role);
              } else if (message.data.challenger === this.userId && !url.searchParams.has('role')) {
                url.searchParams.set('role', 'challenger');
              }

              // Get the full URL with all parameters
              gameUrl = url.toString();
              console.log('Enhanced game URL with missing parameters:', gameUrl);
            }

            // Add random timestamp to URL to prevent caching
            if (gameUrl.includes('?')) {
              gameUrl += `&_t=${Date.now()}`;
            } else {
              gameUrl += `?_t=${Date.now()}`;
            }

            console.log('Redirecting to game URL:', gameUrl);

            // Use a single, reliable method for navigation
            window.location.href = gameUrl;
          } catch (error) {
            console.error('Error redirecting to game:', error);

            // Last resort fallback - try setting window.location directly
            try {
              window.location = message.data.url;
            } catch (e) {
              console.error('All redirect methods failed:', e);
            }
          }
        } else {
          console.error('Game launch message missing URL', message);
        }
        break;

      case 'GAME_START_BROADCAST':
        // Game start broadcast (fallback when direct messages fail)
        console.log('GAME_START_BROADCAST RECEIVED:', message.data);

        // Emit the event first so subscribers can react
        this.emit('gameStartBroadcast', message.data);

        // Check if this broadcast is for current user
        if (message.data && this.userId) {
          const { challenger, rival } = message.data;

          // Normalize user IDs for case-insensitive comparison
          const normalizedUserId = this.userId.toLowerCase();
          const normalizedChallengerId = challenger.userId.toLowerCase();
          const normalizedRivalId = rival.userId.toLowerCase();

          console.log(`User ID check: mine=${normalizedUserId}, challenger=${normalizedChallengerId}, rival=${normalizedRivalId}`);

          if (normalizedChallengerId === normalizedUserId) {
            console.log('This broadcast is for me (challenger)!');

            // Redirect to challenger URL
            window.location.href = challenger.url;
          } else if (normalizedRivalId === normalizedUserId) {
            console.log('This broadcast is for me (rival)!');

            // Redirect to rival URL
            window.location.href = rival.url;
          } else {
            console.log('This broadcast is not for me, ignoring');
          }
        }
        break;

      case 'PONG':
        // Server responded to our ping
        console.log('Received heartbeat pong', message.timestamp);
        // Update the last pong time
        this.lastPongTime = Date.now();
        break;

      case 'ROOM_STATUS_RESET':
        // Custom handler for room status resets to avoid circular updates
        console.log('Room status reset received:', message.data);
        // Using a distinct event type to avoid triggering normal room update handlers
        this.emit('roomStatusReset', message.data);
        break;

      case 'ERROR':
        // Error from server
        console.error('Error from WebSocket server:', message.message);
        this.emit('error', { error: message.message });
        break;

      default:
        console.warn('Unknown WebSocket message type:', message.type);
    }
  }

  /**
   * Create a new room
   * @param {Object} roomData - Room data
   * @returns {boolean} - True if the message was sent successfully
   */
  createRoom(roomData) {
    // Refresh room state first to ensure we have the latest data
    this.refreshRooms(); // Use the throttled method instead of direct message

    return this.sendMessage({
      type: 'CREATE_ROOM',
      data: roomData
    });
  }

  /**
   * Join an existing room
   * @param {string} roomId - Room ID
   * @param {string} userId - User ID
   * @param {string} username - Username
   * @returns {boolean} - True if the message was sent successfully
   */
  joinRoom(roomId, userId, username) {
    return this.sendMessage({
      type: 'JOIN_ROOM',
      data: {
        roomId,
        userId,
        username
      }
    });
  }

  /**
   * Leave a room
   * @param {string} roomId - Room ID
   * @param {string} userId - User ID
   * @returns {boolean} - True if the message was sent successfully
   */
  leaveRoom(roomId, userId) {
    return this.sendMessage({
      type: 'LEAVE_ROOM',
      data: {
        roomId,
        userId
      }
    });
  }

  /**
   * Send a chat message
   * @param {string} roomId - Room ID
   * @param {string} userId - User ID
   * @param {string} username - Username
   * @param {string} message - Chat message
   * @returns {boolean} - True if the message was sent successfully
   */
  sendChatMessage(roomId, userId, username, message) {
    return this.sendMessage({
      type: 'CHAT_MESSAGE',
      data: {
        roomId,
        userId,
        username,
        message
      }
    });
  }

  /**
   * Set participant ready status
   * @param {string} roomId - Room ID
   * @param {string} userId - User ID
   * @param {boolean} isReady - Ready status
   * @returns {boolean} - True if the message was sent successfully
   */
  setReadyStatus(roomId, userId, isReady) {
    return this.sendMessage({
      type: 'READY_STATUS',
      data: {
        roomId,
        userId,
        isReady
      }
    });
  }

  /**
   * Start a game with ready players
   * @param {string} roomId - Room ID
   * @param {string} userId - User ID of the room creator
   * @returns {boolean} - True if the message was sent successfully
   */
  lastGameStartTimestamp = 0;
  startGame(roomId, userId) {
    return new Promise((resolve, reject) => {
      console.log(`Sending START_GAME for room ${roomId}, user ${userId}`);

      // Throttle game start requests to prevent excessive requests
      const currentTime = Date.now();
      if (currentTime - this.lastGameStartTimestamp < 3000) {
        console.log('Throttling START_GAME request - too soon since last request');
        reject(new Error('Too many requests. Please wait a moment before trying again.'));
        return;
      }

      this.lastGameStartTimestamp = currentTime;

      // Show loading indicator or toast to indicate game is starting
      try {
        // If toast is available, show starting message
        if (typeof toast !== 'undefined') {
          toast.loading('Starting game, please wait...', {
            duration: 5000 // Show for 5 seconds
          });
        }
      } catch (e) {
        console.log('Toast notification not available:', e);
      }

      // First refresh the room state to ensure we have the latest data
      this.refreshRooms(); // Use the throttled method instead of direct message

      // Register one-time event listeners to handle responses
      const handleGameLaunch = (data) => {
        console.log('Game launch received in startGame handler:', data);
        this.off('gameLaunch', handleGameLaunch);
        this.off('error', handleError);
        resolve({ success: true, data });
      };

      const handleError = (error) => {
        console.error('Error received in startGame handler:', error);
        this.off('gameLaunch', handleGameLaunch);
        this.off('error', handleError);
        reject(error);
      };

      // Set timeout to fail the promise if no response is received
      const timeoutId = setTimeout(() => {
        this.off('gameLaunch', handleGameLaunch);
        this.off('error', handleError);
        console.log('Game start request timed out - resolving with auto-success');
        // Resolve anyway to avoid blocking the UI
        resolve({ success: true, message: 'Request sent but no server confirmation received' });
      }, 5000); // 5 second timeout

      // Register temporary event listeners
      this.on('gameLaunch', handleGameLaunch);
      this.on('error', handleError);

      // Then send the start game request
      const messageSent = this.sendMessage({
        type: 'START_GAME',
        data: {
          roomId,
          userId
        }
      });

      if (!messageSent) {
        clearTimeout(timeoutId);
        this.off('gameLaunch', handleGameLaunch);
        this.off('error', handleError);
        reject(new Error('Failed to send game start request'));
        return;
      }

      // Set a fallback timeout in case the server response is lost
      // Only send one more request with a longer delay
      setTimeout(() => {
        console.log('Sending a second START_GAME request as fallback');
        this.lastGameStartTimestamp = Date.now(); // Update timestamp to avoid throttling
        this.sendMessage({
          type: 'START_GAME',
          data: {
            roomId,
            userId
          }
        });
      }, 3000);
    });
  }

  /**
   * Add an event listener
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   * @returns {WebSocketRoomClient} - Current instance for chaining
   */
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = new Set();
    }
    this.listeners[event].add(callback);
    return this;
  }

  /**
   * Remove an event listener
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   * @returns {WebSocketRoomClient} - Current instance for chaining
   */
  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].delete(callback);
    }
    return this;
  }

  /**
   * Emit an event to all listeners
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${event} event handler:`, error);
        }
      });
    }
  }
}

// Create a singleton instance
const websocketRoomClient = new WebSocketRoomClient();

export default websocketRoomClient;
